import { useEffect, useRef } from 'react'
import Icon from './Icon'
import styles from './ConfirmDialog.module.css'

/**
 * ConfirmDialog — generic in-product confirmation overlay.
 *
 * A small dialog the product reuses anywhere an action needs an explicit
 * confirm/cancel. It renders props only (component layer) — the caller owns the
 * copy and decides what each button does. Stacks above other modals.
 *
 * Behaviour: backdrop click & Escape → onCancel; Enter → onConfirm; the confirm
 * button is autofocused so a confirmed keypress is the default path.
 *
 * Props:
 *   title        {string}              — short heading line
 *   message      {string}              — body copy
 *   confirmLabel {string}              — confirm button text (default 'Confirm')
 *   cancelLabel  {string}              — cancel button text (default 'Cancel')
 *   variant      {'primary'|'danger'}  — confirm button style (default 'primary')
 *   icon         {string}              — optional Icon name (e.g. 'warning'). When
 *     set, the message renders inside a tinted banner with that icon instead of
 *     plain text — reserved for confirmations with real consequences (e.g.
 *     permanent data loss), not every danger-variant dialog. Most callers should
 *     leave this unset.
 *   confirmIcon  {string}              — optional Icon name shown inside the
 *     confirm button itself (e.g. 'trash' for a destructive removal). Unset by
 *     default — most callers just use text.
 *   onConfirm    {function}
 *   onCancel     {function}
 */
export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  icon,
  confirmIcon,
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null)

  useEffect(() => {
    confirmRef.current?.focus()
    function onKey(e) {
      if (e.key === 'Escape') onCancel()
      else if (e.key === 'Enter') onConfirm()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onConfirm, onCancel])

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onCancel()
  }

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <h2 className={styles.title}>{title}</h2>
        {icon ? (
          <div className={`${styles.messagePanel} ${variant === 'danger' ? styles.messagePanelDanger : styles.messagePanelWarn}`}>
            <span className={styles.messagePanelIcon}><Icon name={icon} /></span>
            <p className={styles.messagePanelText}>{message}</p>
          </div>
        ) : (
          <p className={styles.message}>{message}</p>
        )}
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            className={`${styles.confirmBtn} ${variant === 'danger' ? styles.danger : ''}`}
            onClick={onConfirm}>
            {confirmIcon && <Icon name={confirmIcon} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
